import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, TextInput, Button, Title, Text } from '@tremor/react';
import { useAuth } from '../hooks/useAuth';
import { LoginResult, forgotPassword, confirmForgotPassword } from '../services/auth';

type Mode = 'login' | 'newPassword' | 'forgotPassword';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [pending, setPending] = useState<Extract<LoginResult, { type: 'newPasswordRequired' }> | null>(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  // Deep-link from the password-reset email: /login?reset=1&email=...&code=...
  // Opens the reset screen with email + code pre-filled so the user only sets a new password.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wantsReset = params.get('reset') === '1' || params.has('code');
    if (!wantsReset) return;
    const em = params.get('email');
    const cd = params.get('code');
    if (em) setEmail(em);
    if (cd) { setCode(cd); setCodeSent(true); }
    setMode('forgotPassword');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.type === 'newPasswordRequired') {
        setPending(result);
        setMode('newPassword');
      } else {
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('הסיסמאות לא תואמות');
      return;
    }
    if (!pending) return;
    setLoading(true);
    try {
      await pending.complete(newPassword);
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to set new password');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async () => {
    setError(''); setInfo('');
    if (!email) { setError('הזן כתובת מייל קודם'); return; }
    setLoading(true);
    try {
      await forgotPassword(email);
      setCodeSent(true);
      setInfo('קוד נשלח למייל. אם כבר קיבלת קוד מהאדמין, אפשר להשתמש בכל אחד מהם.');
    } catch (err: any) {
      setError(err.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('הסיסמאות לא תואמות');
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(email, code, newPassword);
      setInfo('הסיסמה אופסה. מתחבר...');
      // Auto-login with new password
      const result = await login(email, newPassword);
      if (result.type === 'success') navigate('/');
      else setMode('login');
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const goToForgot = () => {
    setMode('forgotPassword');
    setError(''); setInfo(''); setCode(''); setNewPassword(''); setConfirmPassword('');
    setCodeSent(false);
  };

  const goBackToLogin = () => {
    setMode('login');
    setError(''); setInfo(''); setCode(''); setNewPassword(''); setConfirmPassword('');
    setCodeSent(false); setPending(null);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <Title>Dashboards</Title>

        {mode === 'newPassword' && (
          <>
            <Text className="mt-1 mb-6">קביעת סיסמה חדשה לסיום ההתחברות</Text>
            <form onSubmit={handleSetNewPassword} className="space-y-4">
              <TextInput placeholder="סיסמה חדשה" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              <TextInput placeholder="אישור סיסמה חדשה" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              {error && <Text className="text-red-500">{error}</Text>}
              <Button type="submit" loading={loading} className="w-full">קבע סיסמה והתחבר</Button>
            </form>
          </>
        )}

        {mode === 'forgotPassword' && (
          <>
            <Text className="mt-1 mb-6">איפוס סיסמה — הזן את הקוד שקיבלת במייל וסיסמה חדשה</Text>
            <form onSubmit={handleConfirmReset} className="space-y-4">
              <TextInput placeholder="מייל" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <Button
                type="button"
                variant="secondary"
                onClick={handleSendCode}
                loading={loading && !codeSent}
                className="w-full"
              >
                {codeSent ? 'שלח קוד שוב' : 'שלח לי קוד חדש'}
              </Button>
              <TextInput placeholder="קוד מהמייל" type="text" value={code} onChange={(e) => setCode(e.target.value)} required />
              <TextInput placeholder="סיסמה חדשה" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
              <TextInput placeholder="אישור סיסמה חדשה" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
              {info && <Text className="text-blue-600 text-sm">{info}</Text>}
              {error && <Text className="text-red-500">{error}</Text>}
              <Button type="submit" loading={loading && codeSent} className="w-full">אפס סיסמה והתחבר</Button>
              <button type="button" onClick={goBackToLogin} className="text-sm text-gray-500 underline w-full">חזור להתחברות</button>
            </form>
          </>
        )}

        {mode === 'login' && (
          <>
            <Text className="mt-1 mb-6">Sign in to your account</Text>
            <form onSubmit={handleSubmit} className="space-y-4">
              <TextInput placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <TextInput placeholder="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              {error && <Text className="text-red-500">{error}</Text>}
              <Button type="submit" loading={loading} className="w-full">Sign In</Button>
              <button type="button" onClick={goToForgot} className="text-sm text-blue-600 underline w-full">שכחתי סיסמה</button>
            </form>
          </>
        )}
      </Card>
    </div>
  );
}
